import { useMemo, useEffect, useState } from 'react';
import ActionButtons from '@shared/components/ActionButtons';
import Table from '@shared/components/Table';
import { COMMON_BUTTONS } from '@config/actionButtonConfigs';
import { calendarService } from '../services/calendar';
import { useTranslation } from 'react-i18next';
import CreateCalendarModal from './CreateCalendarModal';

export default function Calendar() {
    const { t, i18n } = useTranslation();
    const [activeView, setActiveView] = useState('calendar');
    const [calendarData, setCalendarData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedMonth, setSelectedMonth] = useState(() => new Date().getMonth());
    const [selectedYear, setSelectedYear] = useState(() => new Date().getFullYear());
    const [showCreateModal, setShowCreateModal] = useState(false);

    const YEARS = Array.from({ length: 10 }, (_, index) => new Date().getFullYear() + index);

    const MONTHS = [
        t('common.monthsList.january'),
        t('common.monthsList.february'),
        t('common.monthsList.march'),
        t('common.monthsList.april'),
        t('common.monthsList.may'),
        t('common.monthsList.june'),
        t('common.monthsList.july'),
        t('common.monthsList.august'),
        t('common.monthsList.september'),
        t('common.monthsList.october'),
        t('common.monthsList.november'),
        t('common.monthsList.december')
    ];

    const WEEKDAYS = [
        t('common.weekdaysList.monday'),
        t('common.weekdaysList.tuesday'),
        t('common.weekdaysList.wednesday'),
        t('common.weekdaysList.thursday'),
        t('common.weekdaysList.friday'),
        t('common.weekdaysList.saturday'),
        t('common.weekdaysList.sunday')
    ];


    const fetchCalendarData = async (year, month) => {
        setLoading(true);
        setError(null);
        try {
            const response = await calendarService.getCalendar(year, month);
            if (response.length === 0) {
                setShowCreateModal(true);
            }
            setCalendarData(response);
        } catch (error) {
            setError(error.response?.data?.message || 'Error fetching calendar');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateCalendar = async () => {
        setLoading(true);
        setError(null);
        try {
            await calendarService.createCalendar(selectedYear, selectedMonth + 1);
            setShowCreateModal(false);
            // Refetch the calendar data after creation
            await fetchCalendarData(selectedYear, selectedMonth + 1);
        } catch (error) {
            // Extract error message from backend response
            const errorMessage = error.response?.data 
                || error.response?.data?.error 
                || error.message 
                || error.response?.data?.message
                || 'Error creating calendar';
            setError(errorMessage);
            setShowCreateModal(false);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        if (selectedYear !== null && selectedMonth !== null) {
            setError(null); // Clear error when changing month/year
            fetchCalendarData(selectedYear, selectedMonth + 1);
        }
    }, [selectedYear, selectedMonth]);

    const formatDayLabel = (day) => {
        const currentLang = i18n.language;
        if (currentLang === 'bg') {
            if (day === 1 || day === 21 || day === 31) return `${day}-ви`;
            if (day === 2 || day === 22) return `${day}-ри`;
            if (day === 3 || day === 23) return `${day}-ти`;
            return `${day}-ти`;
        } else {
            if (day === 1 || day === 21 || day === 31) return `${day}st`;
            if (day === 2 || day === 22) return `${day}nd`;
            if (day === 3 || day === 23) return `${day}rd`;
            return `${day}th`;
        }
    };

    const mapStatus = (status) => {
        const statusMap = {
            "FEAST": "publicHoliday",
            "WORK": "working",
            "NOWORK": "holiday"
        }
        return statusMap[status] || "working";
    }

    const mapStatusToApi = (status) => {
        const statusMap = {
            "publicHoliday": "FEAST",
            "working": "WORK",
            "holiday": "NOWORK"
        }
        return statusMap[status] || "WORK";
    }

    const handleDayStatusChange = async (dayId, newStatus) => {
        if (!dayId) {
            setError('Cannot update day without ID');
            return;
        }

        const apiStatus = mapStatusToApi(newStatus);
        try {
            await calendarService.updateCalendarDay(dayId, apiStatus);
            // Refetch calendar data to get updated status
            await fetchCalendarData(selectedYear, selectedMonth + 1);
        } catch (error) {
            const errorMessage = error.response?.data 
                || error.response?.data?.error 
                || error.message 
                || error.response?.data?.message
                || 'Error updating calendar day';
            setError(errorMessage);
        }
    };

    const transformCalendarData = (apiData, year, month) => {
        const dayStatusMap = {};
        apiData.forEach(item => {
            const [yearStr, monthStr, dayStr] = item.dateDay.split('-');
            const date = new Date(parseInt(yearStr), parseInt(monthStr) - 1, parseInt(dayStr)); const day = date.getDate();
            dayStatusMap[day] = {
                id: item.id,
                status: mapStatus(item.dateStatus),
                dayLabel: formatDayLabel(day)
            };
        });

        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();

        let firstDayWeekday = firstDay.getDay();
        firstDayWeekday = firstDayWeekday === 0 ? 6 : firstDayWeekday - 1; // Convert to Monday=0 format

        const weeks = [];
        let currentWeek = [];

        for (let i = 0; i < firstDayWeekday; i++) {
            currentWeek.push({ dayLabel: '', status: 'empty' });
        }

        for (let day = 1; day <= daysInMonth; day++) {
            const dayData = dayStatusMap[day] || {
                id: null,
                status: 'working',
                dayLabel: formatDayLabel(day)
            };
            currentWeek.push(dayData);

            if (currentWeek.length === 7) {
                weeks.push(currentWeek);
                currentWeek = [];
            }
        }

        while (currentWeek.length > 0 && currentWeek.length < 7) {
            currentWeek.push({ dayLabel: '', status: 'empty' });
        }

        if (currentWeek.length > 0) {
            weeks.push(currentWeek);
        }

        return weeks;
    };

    const calendarGrid = useMemo(() => {
        if (!calendarData || calendarData.length === 0) {
            return [];
        }
        return transformCalendarData(calendarData, selectedYear, selectedMonth);
    }, [calendarData, selectedYear, selectedMonth]);

    const holidaysData = useMemo(() => ([
        { id: 1, date: '2025-11-01', weekday: 'Събота', postalCode: '1000', location: 'София' },
        { id: 2, date: '2025-11-02', weekday: 'Неделя', postalCode: '9000', location: 'Варна' },
        { id: 3, date: '2025-11-08', weekday: 'Събота', postalCode: '7000', location: 'Русе' },
        { id: 4, date: '2025-11-09', weekday: 'Неделя', postalCode: '6000', location: 'Стара Загора' },
        { id: 5, date: '2025-11-15', weekday: 'Събота', postalCode: '4000', location: 'Пловдив' },
        { id: 6, date: '2025-11-16', weekday: 'Неделя', postalCode: '5000', location: 'Велико Търново' },
    ]), []);

    const holidaysColumns = useMemo(() => ([
        { key: 'date', label: t('additionalSettings.calendar.table.date') },
        { key: 'weekday', label: t('additionalSettings.calendar.table.weekday') },
        { key: 'postalCode', label: t('additionalSettings.calendar.table.postalCode') },
        { key: 'location', label: t('additionalSettings.calendar.table.location') },
    ]), []);

    const tableActionButtons = useMemo(() => (
        <ActionButtons
            buttons={[
                { ...COMMON_BUTTONS.ADD, onClick: () => { } },
                { ...COMMON_BUTTONS.DELETE, onClick: () => { } },
                { ...COMMON_BUTTONS.SAVE, onClick: () => { } },
                { ...COMMON_BUTTONS.CANCEL, onClick: () => setActiveView('calendar') },
            ]}
        />
    ), [setActiveView]);

    const searchOptions =
        [
            { key: 'postalCode', label: 'postalCode' },
            { key: 'title', label: 'title' },
            { key: 'year', label: 'year' },
        ]

    const noop = () => { };

    return (
        <div className="calendar-wrapper width-100">
            <h1>{t('additionalSettings.calendar.title')}</h1>
            <header className="calendar-header">
                <div className="calendar-tabs">
                    <button
                        className={`calendar-tab ${activeView === 'calendar' ? 'calendar-tab--active' : ''}`}
                        onClick={() => setActiveView('calendar')}
                    >
                        {t('additionalSettings.calendar.table.headers.companyCalendar')}
                    </button>
                    <button
                        className={`calendar-tab ${activeView === 'regional' ? 'calendar-tab--active' : ''}`}
                        onClick={() => setActiveView('regional')}
                    >
                        {t('additionalSettings.calendar.table.headers.regionalHolidays')}
                    </button>
                </div>
                <div className="calendar-controls">
                    {activeView === 'calendar' && (

                        <label className="calendar-control">
                            <span>{t('additionalSettings.calendar.table.month')}</span>
                            <select defaultValue={selectedMonth} onChange={(e) => setSelectedMonth(Number(e.target.value))}>
                                {MONTHS.map((month, index) => (
                                    <option key={month} value={index}>{month}</option>
                                ))}
                            </select>
                        </label>
                    )}
                    {activeView === 'calendar' && (
                        <label className="calendar-control">
                            <span>{t('additionalSettings.calendar.table.year')}</span>
                            <select defaultValue={selectedYear} onChange={(e) => setSelectedYear(Number(e.target.value))}>
                                {YEARS.map((year) => (
                                    <option key={year} value={year}>{year}</option>
                                ))}
                            </select>
                        </label>
                    )}
                </div>
            </header>

            {activeView === 'calendar' ? (
                <>
                    {error && (
                        <div className="alert alert-danger alert-dismissible" role="alert" style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>{error}</span>
                            <button 
                                type="button" 
                                className="close" 
                                onClick={() => setError(null)}
                                aria-label="Close"
                                style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', padding: '0', marginLeft: '1rem' }}
                            >
                                <span aria-hidden="true">&times;</span>
                            </button>
                        </div>
                    )}
                    <div className="calendar-table">
                        <div className="calendar-table__header">
                        {WEEKDAYS.map((day) => (
                            <div key={day} className="calendar-table__header-cell">
                                {day}
                            </div>
                        ))}
                    </div>

                    <div className="calendar-table__body">
                        {calendarGrid.map((week, weekIndex) => (
                            <div key={weekIndex} className="calendar-table__row">
                                {week.map((cell, cellIndex) => (
                                    <div
                                        key={`${weekIndex}-${cellIndex}`}
                                        className={`calendar-table__cell calendar-table__cell--${cell.status}`}
                                    >
                                        {cell.dayLabel && (
                                            <>
                                                <span className="calendar-table__day">{cell.dayLabel}</span>
                                                <select 
                                                    style={{ width: '50%' }} 
                                                    value={cell.status}
                                                    onChange={(e) => handleDayStatusChange(cell.id, e.target.value)}
                                                >
                                                    <option value="holiday">{t('additionalSettings.calendar.table.holidays')}</option>
                                                    <option value="working">{t('additionalSettings.calendar.table.workingDays')}</option>
                                                    <option value="publicHoliday">{t('additionalSettings.calendar.table.publicHolidays')}</option>
                                                </select>
                                            </>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>

                    <div className="calendar-table__footer">
                        <label className="calendar-footer__option">
                            <input type="checkbox" />
                            <span>{t('additionalSettings.calendar.table.saveReport')}</span>
                        </label>
                    </div>
                </div>
                </>
            ) : (
                <Table
                    data={holidaysData}
                    columns={holidaysColumns}
                    loading={loading}
                    error={error}
                    selectedData={null}
                    pageNumber={0}
                    pageSize={holidaysData.length}
                    totalElements={holidaysData.length}
                    totalPages={1}
                    sortBy=""
                    sortDir="asc"
                    searchQuery=""
                    searchType=""
                    searchOptions={searchOptions}
                    actionButtons={tableActionButtons}
                    handleSort={noop}
                    handleCheckboxChange={null}
                    handleRowClick={null}
                    handleSearchClick={noop}
                    setSearchQuery={noop}
                    setSearchType={noop}
                    setPageSize={noop}
                    setpageNumber={noop}
                    getData={noop}
                    showPageSizeSelector={false}
                    showPagination={false}
                    showPreviousPage={false}
                    showCustomSearch={false}
                />
            )}
            {showCreateModal && (
                <CreateCalendarModal
                    onClose={() => setShowCreateModal(false)}
                    onConfirm={handleCreateCalendar}
                    monthName={MONTHS[selectedMonth]}
                    year={selectedYear}
                />
            )}
        </div>
    );
}